import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import Button from '@/storybook/components/Button/Button';
import Dialog from '@/storybook/components/Dialog/Dialog';
import Checkbox from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import Input from '@/storybook/components/FormCollection/Input/Input';
import { UserFilterListItem } from '@/models/user/userFilter';
import useFilterNameForm, { FILTER_SET_NAME_MAX_LENGTH } from '@/hooks/useFilterNameForm';

import styles from './RenameFilterDialog.module.css';

interface RenameFilterDialogProps {
  isOpen: boolean;
  isLoading: boolean;
  filter: UserFilterListItem | null;
  onSave: (filterName: string, isDefault: boolean) => void | Promise<void>;
  onCancel: () => void;
}

const RenameFilterDialog: FC<RenameFilterDialogProps> = ({ isOpen, isLoading, filter, onSave, onCancel }) => {
  const { t } = useTranslation();

  const { isSaveDisabled, formMethods, handleDefaultChange, handleSave, reset, isDefault } = useFilterNameForm();

  useEffect(() => {
    if (isOpen && filter) {
      reset(filter.filterName ?? '', filter.isDefault ?? false);
    }
  }, [isOpen, filter, reset]);

  const handleSubmit = useCallback(() => {
    handleSave(onSave);
  }, [handleSave, onSave]);

  const footer = (
    <>
      <Button variant="outlined" onClick={onCancel} disabled={isLoading} data-testid="rename-filter-cancel">
        {t('global.filters.manageSavedFilters.renameCancel')}
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isSaveDisabled || isLoading}
        loading={isLoading}
        data-testid="rename-filter-save"
      >
        {t('global.filters.manageSavedFilters.renameSave')}
      </Button>
    </>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      title={t('global.filters.manageSavedFilters.renameTitle')}
      size="S"
      footer={footer}
      data-testid="rename-filter-dialog"
    >
      <div>
        <FormProvider {...formMethods}>
          <Input
            name="filterSetName"
            label={t('global.filters.manageSavedFilters.renameLabel')}
            labelPosition="top"
            registerOptions={{ maxLength: FILTER_SET_NAME_MAX_LENGTH }}
            maxLength={FILTER_SET_NAME_MAX_LENGTH}
            data-testid="rename-filter-name-input"
          />
        </FormProvider>
      </div>

      <Checkbox
        checked={isDefault}
        onChange={handleDefaultChange}
        label={t('global.filters.saveFiltersDialog.setAsDefault')}
        className={styles.defaultCheckbox}
        data-testid="rename-filter-default-checkbox"
      />
    </Dialog>
  );
};

export default RenameFilterDialog;
